import React, { useEffect, useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import './Sidebar.css'
import '../branding/brand.css'
import { toast } from 'react-toastify'
import { useSelector, useDispatch } from 'react-redux'
import { logoutAsync } from '../auth/authSlice'
import { fetchgetProfileApi } from '../Redux/AffiliateSlices/ProfileSlice'
import { ADMIN_NAV_ITEMS } from '../utils/adminNavConfig'
import { canAccessAny, getUserPermissionsFromAuth, isAffiliateRole } from '../utils/userPermissions'
import { getRoleName, normalizeRoleName, getLoginPathByRole } from '../utils/auth'

import {
  LayoutDashboard,
  Home,
  Users,
  UsersRound,
  ShieldCheck,
  Building2,
  FileText,
  FileWarning,
  ClipboardList,
  Share2,
  RotateCcw,
  Link2,
  GraduationCap,
  BookOpen,
  CreditCard,
  Receipt,
  Megaphone,
  Send,
  Ticket,
  Bell,
  Newspaper,
  UserCheck,
  FolderTree,
  Tag as TagIcon,
  Image as ImageIcon,
  Mail,
  HelpCircle,
  Headphones,
  MessageCircle,
  BarChart3,
  Settings as SettingsIcon,
  GitBranch,
  Code2,
  ChevronDown,
  Activity,
  PlusCircle,
  Handshake,
  Calendar,
  UserPlus,
} from 'lucide-react';

const NAV_ICONS = {
  // Parent group headers
  'Dashboard': Home,
  'User & Team Management': UsersRound,
  'Property & Tax Management': Home,
  'Referral Management': Share2,
  'Training & Resources': GraduationCap,
  'Finance & Payments': CreditCard,
  'Marketing & Engagement': Megaphone,
  'Content Management': FileText,
  'Support': Headphones,
  'Reports & Analytics': BarChart3,
  'System Management': SettingsIcon,

  // Direct routes & subitems matching screenshot
  '/admin/dashboard': Home,
  '/admin/sales-dashboard': LayoutDashboard,
  '/super-admin-roles': ShieldCheck,
  '/users': Users,
  '/recruiters': UsersRound,
  '/admin/partners': ShieldCheck,
  '/admin/properties': Building2,
  '/admin/property-report': FileText,
  '/admin/homestead': Home,
  '/admin-protest': FileWarning,
  '/admin/estimate-requests': ClipboardList,
  '/referral': Users,
  '/referral-request': RotateCcw,
  '/admin/referral-codes': Link2,
  '/training-library': BookOpen,
  '/withdraw-request': CreditCard,
  '/admin/refund-invoices': Receipt,
  '/campaign': Send,
  '/campaign-leadstep': Megaphone,
  '/admin-coupons': Ticket,
  '/push-notification': Bell,
  '/admin/blogs': BookOpen,
  '/admin/blogs/new': PlusCircle,
  '/admin/news': Newspaper,
  '/admin/news/new': PlusCircle,
  '/admin/updates': Activity,
  '/admin/updates/new': PlusCircle,
  '/admin/authors': UserCheck,
  '/admin/authors/new': PlusCircle,
  '/admin/categories': FolderTree,
  '/admin/tags': TagIcon,
  '/admin/media-library': ImageIcon,
  '/admin/pages': FileText,
  '/admin/comments': MessageCircle,
  '/admin/subscribers': Mail,
  '/admin/cms-settings': SettingsIcon,
  '/admin/faqs': HelpCircle,
  '/admin-chat': Bell,
  '/affiliate-chat': MessageCircle,
  '/admin-analytics': BarChart3,
  '/versions': Code2,
  '/admin-grafana': Activity,
  '/admin-grafana/server': Activity,
  '/admin-grafana/containers': Activity,
  '/admin/recruiter-meetings': Calendar,
};

function Sidebar({ isOpen, onToggleSidebar }) {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [expandedMenus, setExpandedMenus] = useState({});

  useEffect(() => {
    dispatch(fetchgetProfileApi())
  }, [dispatch])

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isOpen]);

  const auth = useSelector((state) => state.auth)
  const { user, role } = auth
  const userPermissions = getUserPermissionsFromAuth(auth)

  const firstInitial = user?.user?.first_name?.[0]?.toUpperCase() || '';
  const lastInitial = user?.user?.last_name?.[0]?.toUpperCase() || '';
  const userInitials = `${firstInitial}${lastInitial}`.trim();

  const roleName = normalizeRoleName(getRoleName(role));
  const isAffiliateUser = isAffiliateRole(role);
  const isAdmin = roleName === 'admin';
  const isSuperAdmin = roleName === 'super_admin';

  const checkItemAccess = (perms) => {
    if (isSuperAdmin) return true;
    return canAccessAny(userPermissions, perms);
  };
  const visibleAdminNavItems = ADMIN_NAV_ITEMS.filter((item) =>
    item.children?.length
      ? item.children.some((child) => checkItemAccess(child.permissions))
      : checkItemAccess(item.permissions)
  );
  console.log(visibleAdminNavItems, "visibleAdminNavItems342342")
  const fullName = `${user?.user?.first_name || ''} ${user?.user?.last_name || ''}`.trim();
  const displayName = (isAdmin || isSuperAdmin) ? (user?.user?.first_name || (isSuperAdmin ? 'Super Admin' : 'Admin')) : fullName;
  const avatarInitials = (isAdmin || isSuperAdmin) ? (firstInitial || (isSuperAdmin ? 'SA' : 'A')) : userInitials;

  const { Profiledata } = useSelector(state => state.AffiliateProfile);

  useEffect(() => {
    const nextExpanded = {};
    ADMIN_NAV_ITEMS.forEach((item) => {
      if (!item.children?.length) return;
      const isChildActive = item.children.some((child) => location.pathname === child.to);
      if (isChildActive) nextExpanded[item.label] = true;
    });
    if (Object.keys(nextExpanded).length) {
      setExpandedMenus((prev) => ({ ...prev, ...nextExpanded }));
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    if (window.innerWidth <= 768 && onToggleSidebar) {
      onToggleSidebar();
    }

    const loginPath = getLoginPathByRole(role);

    try {
      await dispatch(logoutAsync()).unwrap();
      toast.success('Logout successful!');
    } catch {
      toast.info('Session ended.');
    } finally {
      navigate(loginPath, { replace: true });
    }
  };

  const handleNavClick = () => {
    if (window.innerWidth <= 768 && onToggleSidebar) {
      onToggleSidebar();
    }
  };

  const toggleSubmenu = (label) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: prev[label] !== undefined ? !prev[label] : false,
    }));
  };

  const renderAdminNavItem = (item) => {
    if (item.children?.length) {
      const visibleChildren = item.children.filter((child) =>
        checkItemAccess(child.permissions)
      );
      if (!visibleChildren.length) return null;

      const isExpanded = expandedMenus[item.label] !== undefined ? !!expandedMenus[item.label] : true;
      const isChildActive = visibleChildren.some((child) => location.pathname === child.to);
      const ParentIcon = NAV_ICONS[item.label] || NAV_ICONS[visibleChildren[0]?.to] || LayoutDashboard;

      return (
        <div key={item.label} className="sidebar-group">
          <button
            type="button"
            className={`sidebar-item sidebar-parent${isChildActive ? ' child-active' : ''}`}
            onClick={() => toggleSubmenu(item.label)}
            aria-expanded={isExpanded}
          >
            <ParentIcon className="sidebar-icon" size={20} />
            <span>{item.label}</span>
            <ChevronDown
              className={`sidebar-chevron${isExpanded ? ' open' : ''}`}
              size={16}
            />
          </button>
          {isExpanded && (
            <div className="sidebar-submenu">
              {visibleChildren.map((child) => {
                const ChildIcon = NAV_ICONS[child.to] || LayoutDashboard;
                return (
                  <NavLink
                    key={child.to}
                    to={child.to}
                    className={({ isActive }) => `sidebar-item sidebar-subitem${isActive ? ' active' : ''}`}
                    onClick={handleNavClick}
                  >
                    <ChildIcon className="sidebar-icon" size={18} />
                    <span>{child.label}</span>
                  </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const Icon = NAV_ICONS[item.to] || LayoutDashboard;
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`}
        onClick={handleNavClick}
      >
        <Icon className="sidebar-icon" size={20} />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      {/* Header with close button for mobile */}
      {onToggleSidebar && (
        <div className="sidebar-header">
          <button className="sidebar-close-button" onClick={onToggleSidebar} aria-label="Close sidebar">
            <svg width="18" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
      {!isAffiliateUser && (
        <nav className="sidebar-nav">
          {visibleAdminNavItems.map(renderAdminNavItem)}
          <button className="sidebar-logout-button sidebar-logout-mobile" onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 7L12 10L8 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Logout</span>
          </button>
        </nav>
      )}
      {isAffiliateUser &&
        <nav className="sidebar-nav">
          <NavLink to="/dashboard" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
              <rect x="14" y="3" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
              <rect x="14" y="12" width="7" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" />
              <rect x="3" y="16" width="7" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
            </svg>
            <span>Dashboard</span>
          </NavLink>
          {Profiledata?.personal_info?.affiliate_type === "referral" ?
            <>
              <NavLink to="/referral-links" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
                <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M22 21v-2a4 4 0 0 0-3-3.87" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M16 11.13a4 4 0 0 1 0 7.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span>Recruiters Referral Link</span>
              </NavLink>
              <NavLink to="/user_referral" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
                <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                  <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span>Clients Referral Link</span>
              </NavLink>
            </>
            :
            <NavLink to="/user_referral" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
              <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.5" />
                <line x1="19" y1="8" x2="19" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <line x1="22" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span>Clients Referral Link</span>
            </NavLink>
          }

          {/* /user_referral */}

          {/* <NavLink to="/affiliate-meetings" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <Calendar className="sidebar-icon" size={20} />
            <span>My Meetings</span>
          </NavLink> */}

          <NavLink to="/referrals" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M21 3v5h-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 21v-5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Network Activity</span>
          </NavLink>

          <NavLink to="/forecast" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <BarChart3 className="sidebar-icon" size={20} />
            {/* <span>Analytics & Earnings</span> */}
            <span>My Future Earnings</span>
          </NavLink>

          <NavLink to="/estimate-requests" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <ClipboardList className="sidebar-icon" size={20} />
            <span>Estimate Requests</span>
          </NavLink>

          <NavLink to="/payments" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Earnings & Payouts</span>
          </NavLink>

          <NavLink to="/profile" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <svg className="sidebar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="1.5" />
              <path d="M6.168 18.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.849" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span>Account Settings</span>
          </NavLink>
          <NavLink to="/acknowledgement" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <GraduationCap className="sidebar-icon" size={20} />
            <span>Training Library</span>
          </NavLink>
          <NavLink to="/affiliate/chat" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <MessageCircle className="sidebar-icon" size={20} />
            <span>Team Andy Messages</span>
          </NavLink>
          <NavLink to="/client-outreach" className={({ isActive }) => `sidebar-item${isActive ? ' active' : ''}`} onClick={handleNavClick}>
            <Mail className="sidebar-icon" size={20} />
            <span>Client Outreach</span>
          </NavLink>
          {/* /acknowledgement */}


          <button className="sidebar-logout-button sidebar-logout-mobile" onClick={handleLogout}>
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
              <path d="M8 7L12 10L8 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Logout</span>
          </button>
        </nav>
      }

      <div className="sidebar-footer">
        <div className="sidebar-user-profile">
          <div className="sidebar-user-avatar">
            {avatarInitials ? (
              <span className="sidebar-user-initials" style={{ color: '#fff' }}>{avatarInitials}</span>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="7" r="4" stroke="white" strokeWidth="1.5" />
                <path d="M4 18C4 14.6863 6.68629 12 10 12C13.3137 12 16 14.6863 16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            )}
          </div>
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{displayName}</div>
            <div className="sidebar-user-email">{user?.user?.email}</div>
          </div>
        </div>
        <button className="sidebar-logout-button sidebar-logout-desktop" onClick={handleLogout}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
            <path d="M8 7L12 10L8 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>Logout</span>
        </button>
      </div>
    </div>
  )
}

export default Sidebar
